/**
 * EVO — xodimlar (komissiya / KPI) ma'lumot qatlami.
 *
 * Tiplar `apps/api/src/modules/hr/commission.service.ts` va `kpi.service.ts`
 * dagi javob interfeyslaridan MAYDONMA-MAYDON ko'chirilgan (`lib/
 * replenishment.ts` bilan bir xil naqsh). Bu qo'lda saqlanadigan shartnoma:
 * server ustunni qayta nomlasa TypeScript uni ushlamaydi, shuning uchun har
 * o'zgarish jonli tekshiriladi.
 *
 * ============================================================================
 * PUL — `string`, VA BU YERDA QOIDA ENG QAT'IY
 * ============================================================================
 * `parseFloat` taqiqlangan (`lib/format.ts` fayl boshidagi qoida). Bu ekranda
 * sabab boshqa ekranlardagidan og'irroq: bu yerdagi raqam ODAMNING OYLIGI.
 * Oxirgi xonadagi xato bir xodim uchun tiyin, 20 xodim × 12 oy bo'yicha esa
 * buxgalteriya bilan kadrlar hisobi o'rtasida mos kelmaslik.
 *
 * ============================================================================
 * NIMA UCHUN `basisAmount` `null` BO'LISHI MUMKIN
 * ============================================================================
 * `gross_profit` asosidagi qoidada asos summasi — bu AYNAN marja, va `sales`
 * roli uni TZ §60 bo'yicha ko'rmaydi. Server bunday foydalanuvchiga
 * `basisAmount`, `netBasisAmount` va `effectiveRatePct` ni `null` qilib
 * yuboradi; `netAmount` (to'lanadigan summa) esa har doim keladi — xodim
 * qancha olishini bilishi SHART, aks holda butun modul ma'nosiz.
 *
 * Ekran buni «hisoblanmadi» deb emas, «ko'rsatilmaydi» deb ko'rsatishi kerak.
 */
import { api } from './api';
import type { Page, SortDir } from './products';

// ---------------------------------------------------------------------------
// Lug'atlar — server bilan bir xil
// ---------------------------------------------------------------------------

/** `hr.ts` sxemasi → `COMMISSION_BASIS`. */
export const COMMISSION_BASES = ['revenue', 'gross_profit', 'collection'] as const;
export type CommissionBasis = (typeof COMMISSION_BASES)[number];

export const COMMISSION_CALC_METHODS = ['percent', 'tiered', 'fixed'] as const;
export type CommissionCalcMethod = (typeof COMMISSION_CALC_METHODS)[number];

export const COMMISSION_TIER_MODES = ['marginal', 'flat'] as const;
export type CommissionTierMode = (typeof COMMISSION_TIER_MODES)[number];

export const COMMISSION_RUN_STATUSES = ['draft', 'reviewed', 'closed', 'cancelled'] as const;
export type CommissionRunStatus = (typeof COMMISSION_RUN_STATUSES)[number];

export const COMMISSION_SOURCE_KINDS = ['earning', 'return', 'cancellation', 'overdue'] as const;
export type CommissionSourceKind = (typeof COMMISSION_SOURCE_KINDS)[number];

/**
 * Satr bayroqlari — `commission.calc.ts` → `COMMISSION_FLAGS`.
 *
 * Ro'yxat SHU YERDA takrorlanadi, chunki har bayroqning ekranda o'z rangi va
 * o'z IZOHI bor. Server yangi bayroq qo'shsa, `FLAG_LABELS` dagi `Record`
 * to'liq bo'lmagani uchun **kompilyatsiya yiqiladi** — ya'ni yangi bayroq
 * jimgina e'tibordan chetda qolmaydi (`lib/replenishment.ts` dagi bir xil
 * naqsh va bir xil sabab).
 */
export const COMMISSION_FLAGS = [
  'negative_basis',
  'cap_hit',
  'below_min_basis',
  'high_deduction',
  'zero_commission',
  'no_documents',
] as const;
export type CommissionFlag = (typeof COMMISSION_FLAGS)[number];

export const KPI_METRICS = [
  'sales_volume',
  'gross_profit',
  'collection',
  'new_customers',
  'repeat_customers',
  'avg_order',
  'order_count',
  'return_rate',
] as const;
export type KpiMetric = (typeof KPI_METRICS)[number];

export type KpiUnit = 'money' | 'count' | 'pct';

// ---------------------------------------------------------------------------
// Tiplar
// ---------------------------------------------------------------------------

export interface CommissionTier {
  readonly seq: number;
  readonly fromAmount: string;
  /** `null` — cheksiz, faqat oxirgi zinapoyada. */
  readonly toAmount: string | null;
  readonly ratePct: string;
}

export interface CommissionRuleView {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  readonly roleId: number | null;
  readonly roleCode: string | null;
  readonly userId: number | null;
  readonly userName: string | null;
  readonly basis: CommissionBasis;
  readonly calcMethod: CommissionCalcMethod;
  readonly ratePct: string | null;
  readonly fixedAmount: string | null;
  readonly tierMode: CommissionTierMode;
  readonly tiers: readonly CommissionTier[];
  readonly capAmount: string | null;
  readonly minBasisAmount: string | null;
  readonly deductReturns: boolean;
  readonly returnDeductPct: string;
  readonly deductCancellations: boolean;
  readonly cancelDeductPct: string;
  readonly deductOverdue: boolean;
  readonly overdueDays: number;
  readonly overdueDeductPct: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly priority: number;
  readonly isActive: boolean;
}

export interface CommissionRunHeader {
  readonly id: number;
  readonly docNo: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly runType: 'regular' | 'adjustment';
  readonly correctsRunId: number | null;
  readonly status: CommissionRunStatus;
  readonly currencyCode: string;
  readonly employeeCount: number;
  readonly totalBasisAmount: string;
  readonly totalGrossAmount: string;
  readonly totalDeductionAmount: string;
  readonly totalNetAmount: string;
  readonly notes: string | null;
  readonly closedAt: string | null;
  readonly closedBy: number | null;
}

export interface CommissionLineView {
  readonly id: number;
  readonly userId: number;
  readonly userName: string;
  readonly ruleId: number | null;
  readonly ruleName: string | null;
  readonly basis: CommissionBasis;
  /** `null` — marja maxfiyligi tufayli ko'rsatilmadi (fayl boshidagi izoh). */
  readonly basisAmount: string | null;
  readonly returnAmount: string;
  readonly cancelledAmount: string;
  readonly overdueAmount: string;
  readonly deductionBasisAmount: string;
  readonly netBasisAmount: string | null;
  /** Chegirmasiz variant — ekranda farqni ko'rsatish uchun. */
  readonly rawCommissionAmount: string;
  readonly grossAmount: string;
  readonly deductionAmount: string;
  readonly capApplied: boolean;
  readonly netAmount: string;
  readonly effectiveRatePct: string | null;
  readonly orderCount: number;
  readonly customerCount: number;
  readonly newCustomerCount: number;
  readonly flags: readonly CommissionFlag[];
}

export interface CommissionRunDetail extends CommissionRunHeader {
  readonly lines: readonly CommissionLineView[];
}

export interface CommissionSourceItem {
  readonly kind: CommissionSourceKind;
  readonly sourceTable: string;
  readonly sourceId: number;
  readonly sourceDocNo: string | null;
  readonly sourceDate: string;
  readonly customerId: number | null;
  readonly basisAmount: string;
}

export interface CommissionSources {
  readonly lineId: number;
  readonly basis: CommissionBasis;
  /** `true` — marja maxfiyligi: ro'yxatning O'ZI berkitilgan, summalar emas. */
  readonly hidden: boolean;
  readonly items: readonly CommissionSourceItem[];
}

/** `dryRun: true` javobi — hech narsa yozilmagan. */
export interface CommissionDryRun {
  readonly dryRun: true;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly employeeCount: number;
  readonly grossAmount: string;
  readonly deductionAmount: string;
  readonly netAmount: string;
  readonly lines: readonly CommissionLineView[];
}

export interface MyCommissionPeriod extends CommissionLineView {
  readonly runId: number;
  readonly docNo: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly closedAt: string | null;
}

export interface MyCommission {
  readonly userId: number;
  readonly currency: string;
  readonly periods: readonly MyCommissionPeriod[];
  readonly totalNetAmount: string;
}

export interface KpiPlanLineView {
  readonly metricCode: KpiMetric;
  readonly unit: KpiUnit;
  readonly targetValue: string;
}

export interface KpiPlanView {
  readonly id: number;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly userId: number | null;
  readonly userName: string | null;
  readonly roleId: number | null;
  readonly status: 'draft' | 'active' | 'closed';
  readonly notes: string | null;
  readonly closedAt: string | null;
  readonly lines: readonly KpiPlanLineView[];
}

export interface KpiResultView {
  readonly userId: number;
  readonly userName: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly metricCode: KpiMetric;
  readonly unit: KpiUnit;
  /** `null` — reja qo'yilmagan. NOL EMAS. */
  readonly planValue: string | null;
  readonly actualValue: string;
  readonly achievementPct: string | null;
  /** `null` — reja yo'q, «bajarilmadi» EMAS. */
  readonly isMet: boolean | null;
  readonly isClosed: boolean;
}

// ---------------------------------------------------------------------------
// So'rov kalitlari
// ---------------------------------------------------------------------------

export interface CommissionRunListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortDir: SortDir;
  readonly status?: CommissionRunStatus | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
}

export const commissionKeys = {
  all: ['commission'] as const,
  runs: (params: CommissionRunListParams) => ['commission', 'runs', params] as const,
  run: (id: number) => ['commission', 'run', id] as const,
  sources: (runId: number, lineId: number) => ['commission', 'sources', runId, lineId] as const,
  rules: () => ['commission', 'rules'] as const,
  my: () => ['commission', 'my'] as const,
};

export const kpiKeys = {
  all: ['kpi'] as const,
  plans: (from?: string, to?: string) => ['kpi', 'plans', from ?? null, to ?? null] as const,
  results: (from?: string, to?: string, userId?: number) =>
    ['kpi', 'results', from ?? null, to ?? null, userId ?? null] as const,
  my: () => ['kpi', 'my'] as const,
};

// ---------------------------------------------------------------------------
// Chaqiruvlar
// ---------------------------------------------------------------------------

function toQuery(params: CommissionRunListParams): string {
  const search = new URLSearchParams();
  search.set('page', String(params.page));
  search.set('pageSize', String(params.pageSize));
  search.set('sortDir', params.sortDir);
  if (params.status) search.set('status', params.status);
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  return search.toString();
}

export function fetchCommissionRuns(
  params: CommissionRunListParams,
  signal?: AbortSignal,
): Promise<Page<CommissionRunHeader>> {
  return api.get<Page<CommissionRunHeader>>(`/commission/runs?${toQuery(params)}`, { signal });
}

export function fetchCommissionRun(id: number, signal?: AbortSignal): Promise<CommissionRunDetail> {
  return api.get<CommissionRunDetail>(`/commission/runs/${id}`, { signal });
}

export function fetchCommissionSources(
  runId: number,
  lineId: number,
  signal?: AbortSignal,
): Promise<CommissionSources> {
  return api.get<CommissionSources>(`/commission/runs/${runId}/lines/${lineId}/sources`, { signal });
}

export function fetchCommissionRules(signal?: AbortSignal): Promise<Page<CommissionRuleView>> {
  return api.get<Page<CommissionRuleView>>('/commission/rules?page=1&pageSize=100', { signal });
}

export interface CreateCommissionRunBody {
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly dryRun?: boolean;
  readonly correctsRunId?: number;
  readonly notes?: string;
}

/**
 * Davr hisobi. `dryRun: true` da javob `CommissionDryRun` bo'lib keladi.
 *
 * Ikki javob turi bitta endpointda — bu server tomonidagi ongli qaror
 * (`commission.controller.ts`): sinov va haqiqiy hisob AYNAN bir xil
 * formuladan o'tishi kerak, aks holda sinov hech narsani isbotlamasdi.
 * Ekran ularni `dryRun` maydoni bo'yicha ajratadi.
 */
export function createCommissionRun(
  body: CreateCommissionRunBody,
): Promise<CommissionRunDetail | CommissionDryRun> {
  return api.post<CommissionRunDetail | CommissionDryRun>('/commission/runs', body);
}

export function isDryRun(
  result: CommissionRunDetail | CommissionDryRun,
): result is CommissionDryRun {
  return 'dryRun' in result && result.dryRun === true;
}

export function reviewCommissionRun(id: number, notes?: string): Promise<CommissionRunDetail> {
  return api.post<CommissionRunDetail>(`/commission/runs/${id}/review`, notes ? { notes } : {});
}

export function closeCommissionRun(id: number, notes?: string): Promise<CommissionRunDetail> {
  return api.post<CommissionRunDetail>(`/commission/runs/${id}/close`, notes ? { notes } : {});
}

export function fetchMyCommission(signal?: AbortSignal): Promise<MyCommission> {
  return api.get<MyCommission>('/commission/my', { signal });
}

export function fetchKpiPlans(
  from?: string,
  to?: string,
  signal?: AbortSignal,
): Promise<Page<KpiPlanView>> {
  const search = new URLSearchParams({ page: '1', pageSize: '100' });
  if (from) search.set('from', from);
  if (to) search.set('to', to);
  return api.get<Page<KpiPlanView>>(`/kpi/plans?${search.toString()}`, { signal });
}

export function fetchKpiResults(
  from?: string,
  to?: string,
  userId?: number,
  signal?: AbortSignal,
): Promise<readonly KpiResultView[]> {
  const search = new URLSearchParams();
  if (from) search.set('from', from);
  if (to) search.set('to', to);
  if (userId !== undefined) search.set('userId', String(userId));
  const qs = search.toString();
  return api.get<readonly KpiResultView[]>(`/kpi/results${qs ? `?${qs}` : ''}`, { signal });
}

// ---------------------------------------------------------------------------
// URL holati
// ---------------------------------------------------------------------------

export interface CommissionRunSearch {
  readonly page?: number;
  readonly sortDir?: SortDir;
  readonly status?: CommissionRunStatus;
}

export function parseCommissionRunSearch(search: Record<string, unknown>): CommissionRunSearch {
  const page = Number(search.page);
  const status = COMMISSION_RUN_STATUSES.find((s) => s === search.status);
  const sortDir = search.sortDir === 'asc' ? 'asc' : undefined;

  return {
    ...(Number.isInteger(page) && page > 1 ? { page } : {}),
    ...(status ? { status } : {}),
    ...(sortDir ? { sortDir } : {}),
  };
}

export interface KpiSearch {
  readonly from?: string;
  readonly to?: string;
  readonly userId?: number;
}

export function parseKpiSearch(search: Record<string, unknown>): KpiSearch {
  const isoDate = /^\d{4}-\d{2}-\d{2}$/;
  const from = typeof search.from === 'string' && isoDate.test(search.from) ? search.from : undefined;
  const to = typeof search.to === 'string' && isoDate.test(search.to) ? search.to : undefined;
  const userId = Number(search.userId);

  return {
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(Number.isInteger(userId) && userId > 0 ? { userId } : {}),
  };
}

/**
 * Oyning birinchi va oxirgi kuni.
 *
 * `Date` ARIFMETIKASI ISHLATILMAYDI: `new Date('2026-08-01')` UTC da
 * o'qiladi va Toshkentda bir kun oldinga siljib, iyul oyini beradi. Matn
 * ustidagi amal esa vaqt zonasidan mustaqil — server tomonda ham aynan
 * shu qoida (`commission.calc.ts` `assertPeriod()`).
 */
export function monthBounds(yearMonth: string): { from: string; to: string } {
  const [yearText = '', monthText = ''] = yearMonth.split('-');
  const year = Number(yearText);
  const month = Number(monthText);

  // Oyning oxirgi kuni: keyingi oyning 0-kuni. Bu yerda `Date` faqat KUN
  // SONINI olish uchun ishlatiladi va natija darhol matnga aylanadi —
  // vaqt zonasi bu hisobga ta'sir qilmaydi.
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();

  return {
    from: `${yearMonth}-01`,
    to: `${yearMonth}-${String(lastDay).padStart(2, '0')}`,
  };
}

/** Joriy oy `YYYY-MM` ko'rinishida. */
export function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export interface MyKpiRow {
  readonly userId: number;
  readonly userName: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly metricCode: string;
  readonly unit: string;
  readonly planValue: string | null;
  readonly actualValue: string | null;
  /** Reja yo'q bo'lsa `null` — «0%» EMAS. */
  readonly achievementPct: string | null;
  readonly isMet: boolean | null;
  readonly isClosed: boolean;
}

/**
 * MENING KPI'IM — ruxsatsiz, `ctx.userId` bo'yicha.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN RUXSAT TALAB QILINMAYDI                                       ║
 * ║                                                                          ║
 * ║ `kpi.view` — BOSHQALARNING rejasini ko'rish huquqi va u rahbarda.       ║
 * ║ O'z natijasini esa har kim ko'rishi kerak: aks holda sotuvchi o'z      ║
 * ║ rejasini bilmay ishlaydi va KPI ning butun ma'nosi yo'qoladi.           ║
 * ║                                                                          ║
 * ║ Server ham shunday (`@RequirePermissions()` bo'sh), lekin bu            ║
 * ║ endpointni chaqiradigan ekran yo'q edi.                                 ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export function fetchMyKpi(signal?: AbortSignal): Promise<readonly MyKpiRow[]> {
  return api.get<readonly MyKpiRow[]>('/kpi/my', { signal });
}
